import { useState } from "react";
import { ShieldCheck, Send, Loader2, Search, Lock, FileUp, CheckCircle2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

// Canal de denuncia interna confidencial (43ª-VIII / 49ª) — accesible a cualquier empleado.
export default function CanalDenuncia() {
  const [form, setForm] = useState({ descripcion: "", area_senalada: "", persona_senalada: "", anonimo: true, contra_oc: false });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [folioGenerado, setFolioGenerado] = useState(null);

  const [folioQuery, setFolioQuery] = useState("");
  const [seguimiento, setSeguimiento] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const enviar = async () => {
    if (!form.descripcion.trim()) { toast.error("Describe la conducta u operación."); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (file) {
        const b64 = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = rej;
          r.readAsDataURL(file);
        });
        payload.adjunto_base64 = b64;
        payload.adjunto_nombre = file.name;
        payload.adjunto_mime = file.type;
      }
      const { data } = await api.post("/pld/denuncias", payload);
      setFolioGenerado(data.folio);
      setForm({ descripcion: "", area_senalada: "", persona_senalada: "", anonimo: true, contra_oc: false });
      setFile(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const consultar = async () => {
    if (!folioQuery.trim()) return;
    try {
      const { data } = await api.get(`/pld/denuncias/seguimiento/${folioQuery.trim()}`);
      setSeguimiento(data);
    } catch (e) { setSeguimiento(null); toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Canal de denuncia interna" subtitle="Reporta de forma confidencial al Oficial de Cumplimiento — 43ª-VIII" />
      <div className="grid gap-6 p-6 md:grid-cols-3 md:p-8">
        {/* Formulario */}
        <div className="md:col-span-2 space-y-4 rounded-lg border border-slate-200 bg-white p-5" data-testid="form-denuncia">
          <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
            <Lock className="mt-0.5 h-4 w-4 shrink-0" />
            <p>Tu reporte llega <strong>directamente al Oficial de Cumplimiento</strong>. Tu superior jerárquico no se entera. Si eliges <strong>anónimo</strong>, el sistema no guarda ningún dato que te identifique (ni usuario, ni correo, ni IP).</p>
          </div>

          {folioGenerado ? (
            <div className="space-y-3 rounded-md border border-primary/30 bg-primary/5 p-5 text-center" data-testid="folio-generado">
              <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600" />
              <p className="text-sm text-slate-600">Denuncia recibida. Conserva este folio para dar seguimiento de forma confidencial:</p>
              <p className="font-heading text-2xl font-bold tracking-wide text-primary" data-testid="folio-text">{folioGenerado}</p>
              <Button data-testid="nueva-denuncia-btn" variant="outline" size="sm" onClick={() => setFolioGenerado(null)}>Levantar otra denuncia</Button>
            </div>
          ) : (
            <>
              <div>
                <Label className="mb-1 block text-xs">Descripción de la conducta u operación *</Label>
                <Textarea data-testid="den-descripcion" rows={5} value={form.descripcion} onChange={(e) => set("descripcion", e.target.value)} placeholder="Describe los hechos con el mayor detalle posible…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1 block text-xs">Área señalada (opcional)</Label>
                  <Input data-testid="den-area" value={form.area_senalada} onChange={(e) => set("area_senalada", e.target.value)} />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Persona señalada (opcional)</Label>
                  <Input data-testid="den-persona" value={form.persona_senalada} onChange={(e) => set("persona_senalada", e.target.value)} />
                </div>
              </div>
              <div>
                <Label className="mb-1 block text-xs">Adjunto (opcional, se cifra)</Label>
                <div className="flex items-center gap-2">
                  <FileUp className="h-4 w-4 text-slate-400" />
                  <Input data-testid="den-file" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
                <div>
                  <p className="text-sm font-medium text-slate-700">Denunciar de forma anónima</p>
                  <p className="text-xs text-slate-500">Si lo desactivas, el OC verá tu nombre y podrás seguir tu denuncia desde tu panel.</p>
                </div>
                <Switch data-testid="den-anonimo" checked={form.anonimo} onCheckedChange={(v) => set("anonimo", v)} />
              </div>
              <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 p-3">
                <div>
                  <p className="text-sm font-medium text-amber-800">La denuncia es contra el propio Oficial de Cumplimiento</p>
                  <p className="text-xs text-amber-700">Se enruta al órgano de administración (vía alterna), no al OC señalado.</p>
                </div>
                <Switch data-testid="den-contra-oc" checked={form.contra_oc} onCheckedChange={(v) => set("contra_oc", v)} />
              </div>
              <Button data-testid="enviar-denuncia-btn" disabled={busy} onClick={enviar} className="w-full bg-primary hover:bg-sidebar-hover">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Enviar denuncia confidencial
              </Button>
            </>
          )}
        </div>

        {/* Seguimiento por folio */}
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="seguimiento-card">
            <h4 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700"><Search className="h-4 w-4" /> Consultar estatus por folio</h4>
            <p className="mb-3 text-xs text-slate-500">Sin revelar tu identidad. Ingresa el folio que recibiste.</p>
            <div className="flex items-center gap-2">
              <Input data-testid="folio-input" placeholder="DEN-XXXXXX-XXXXXXXXXX" value={folioQuery} onChange={(e) => setFolioQuery(e.target.value)} />
              <Button data-testid="consultar-folio-btn" variant="outline" onClick={consultar}>Buscar</Button>
            </div>
            {seguimiento && (
              <div className="mt-3 rounded-md bg-slate-50 p-3 text-sm" data-testid="seguimiento-resultado">
                <p className="text-xs text-slate-500">Folio {seguimiento.folio}</p>
                <div className="mt-1 flex items-center gap-2 text-sm">Estatus: <Badge>{seguimiento.estatus}</Badge></div>
                {seguimiento.convertida_en_caso && <p className="mt-1 text-xs text-emerald-700">Convertida en caso de análisis PLD.</p>}
              </div>
            )}
          </div>
          <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white p-4 text-xs text-slate-500">
            <ShieldCheck className="mt-0.5 h-4 w-4 text-accent" />
            Las denuncias se conservan cifradas por 10 años (51ª) y no se incluyen en exportaciones generales para proteger tu confidencialidad.
          </div>
        </div>
      </div>
    </div>
  );
}
